import { useState, useEffect, useCallback } from "react";
import { logoutUser } from "../utils/logoutUser";

export type UseLogoutProps = {
  /** Success callback */
  onSuccess?: () => void;
  /** Error callback */
  onError?: (error: Error) => void;
  /** Delay before logout (ms), default 500 */
  delay?: number;
  /** Whether to auto-logout on mount */
  autoLogout?: boolean;
};

export type UseLogoutReturn = {
  /** Trigger logout manually */
  logout: () => Promise<void>;
  /** Whether logout is in progress */
  isLoggingOut: boolean;
  /** Whether logout completed */
  isComplete: boolean;
  /** Error if logout failed */
  error: Error | null;
};

/**
 * Composite hook for logout functionality.
 * Handles logout execution with optional auto-trigger on mount.
 *
 * Usage:
 * ```tsx
 * // Auto logout on mount (for logout page)
 * const { isLoggingOut, isComplete, error } = useLogout({
 *   onSuccess: () => navigate('/login'),
 *   onError: (error) => console.error(error),
 *   autoLogout: true,
 * });
 *
 * // Manual logout (for logout button)
 * const { logout, isLoggingOut } = useLogout({
 *   onSuccess: () => navigate('/login'),
 *   autoLogout: false,
 * });
 * ```
 */
export function useLogout({
  onSuccess,
  onError,
  delay = 500,
  autoLogout = true,
}: UseLogoutProps = {}): UseLogoutReturn {
  const [isLoggingOut, setIsLoggingOut] = useState(autoLogout);
  const [isComplete, setIsComplete] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const logout = useCallback(async () => {
    setIsLoggingOut(true);
    setError(null);

    try {
      await logoutUser();
      setIsComplete(true);
      onSuccess?.();
    } catch (err) {
      const error = err as Error;
      setError(error);
      onError?.(error);
    } finally {
      setIsLoggingOut(false);
    }
  }, [onSuccess, onError]);

  // Auto-logout on mount if enabled
  useEffect(() => {
    if (autoLogout) {
      const timer = setTimeout(() => {
        logout();
      }, delay);

      return () => clearTimeout(timer);
    }
  }, [autoLogout, delay, logout]);

  return {
    logout,
    isLoggingOut,
    isComplete,
    error,
  };
}

export default useLogout;

